'use client';

import {Icon, IconName} from '@components/@icons';
import {MeinText} from '@components/@typography/MeinText';
import classNames from 'classnames';
import {AnimatePresence, motion} from 'framer-motion';
import {useTranslations} from 'next-intl';
import {FC, PropsWithChildren, useEffect, useRef} from 'react';
import styles from './MeinAlert.module.css';

const ICON_NAME_BY_TYPE: Record<IProps['type'], IconName> = {
    danger: 'alert-triangle',
    info: 'information-circle',
    success: 'check-circle',
    warning: 'alert-triangle',
};

interface IProps extends PropsWithChildren {
    type: 'danger' | 'info' | 'success' | 'warning';
    offset?: string;
    isVisible?: boolean;
    className?: string;
    autoFocus?: boolean;
}

export const MeinAlert: FC<IProps> = ({
    type = 'danger',
    children,
    isVisible,
    offset = 0,
    className,
    autoFocus = type === 'danger',
}) => {
    const defaultMessage = useTranslations('global')('genericError', {
        link: '',
    });
    const ref = useRef<HTMLDivElement>(null);
    const iconName = ICON_NAME_BY_TYPE[type];

    useEffect(() => {
        if (autoFocus && (isVisible === undefined || isVisible)) {
            ref.current?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            });
        }
    }, [autoFocus, isVisible]);

    return (
        <AnimatePresence initial={false}>
            {(isVisible === undefined || isVisible) && (
                <motion.div
                    ref={ref}
                    exit={{opacity: 0, height: 0, marginTop: '-' + offset}}
                    initial={{opacity: 0, height: 0, marginTop: '-' + offset}}
                    animate={{opacity: 1, height: 'auto', marginTop: 0}}
                    className={classNames(
                        styles.alert,
                        styles[type],
                        className,
                    )}
                >
                    <div className={styles.inner}>
                        <Icon name={iconName} className={styles.icon} />
                        <MeinText className={styles.p}>
                            {children ?? defaultMessage}
                        </MeinText>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );
};
